import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { toast } from "react-toastify";
import api from "../../api";
import { usePlanWeekDeadlines } from "./usePlanWeekDeadlines";
import { FilterChip, PURPLE } from "./ResourceAllocationShared";

function formatDeadline(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Convert to UK local time for the datetime-local input
  const ukStr = d.toLocaleString("sv-SE", { timeZone: "Europe/London" });
  return ukStr.replace(" ", "T").slice(0, 16);
}

function statusChip(status) {
  if (status === "passed") return { label: "Passed", bg: "#e2e8f0", color: "#475569" };
  if (status === "due_soon") return { label: "Due Soon", bg: "#fef3c7", color: "#b45309" };
  if (status === "upcoming") return { label: "Upcoming", bg: "#dcfce7", color: "#15803d" };
  return { label: "—", bg: "#f1f5f9", color: "#64748b" };
}

export default function DeadlineManagementTab({ selectedPlan, planId }) {
  const { loading, weeks, setWeeks, subjectSchedule } = usePlanWeekDeadlines(planId);
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [savingWeekId, setSavingWeekId] = useState(null);

  useEffect(() => {
    setSubjectFilter("all");
  }, [planId]);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [testingNotify, setTestingNotify] = useState(false);

  const saveOverride = async (weekId, value) => {
    setSavingWeekId(weekId);
    try {
      const r = await api.put(`admin/plan-weeks/${weekId}/deadline`, {
        deadline_override: value || null,
      });
      setWeeks(r.data?.weeks ?? []);
      setOverrideDrafts((prev) => {
        const next = { ...prev };
        delete next[weekId];
        return next;
      });
      toast.success(value ? "Deadline override saved" : "Override cleared");
    } catch {
      toast.error("Failed to update deadline");
    } finally {
      setSavingWeekId(null);
    }
  };

  const sendTestNotification = async () => {
    if (!planId) return;
    setTestingNotify(true);
    try {
      const r = await api.post(`admin/course-plans/${planId}/deadlines/test-notification`);
      toast.success(r.data?.message || "Test notification sent");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to send test notification");
    } finally {
      setTestingNotify(false);
    }
  };

  const flatRows = useMemo(() => {
    const rows = [];
    weeks.forEach((w) => {
      (w.deadlines_per_subject || []).forEach((sub) => {
        rows.push({ week: w, sub });
      });
    });
    if (subjectFilter === "all") return rows;
    return rows.filter(({ sub }) => String(sub.subject_id) === subjectFilter);
  }, [weeks, subjectFilter]);

  const showSubjectColumn = subjectFilter === "all";

  if (!selectedPlan || !planId) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <EventIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>
          Select a group to manage deadlines
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (!subjectSchedule.length) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" sx={{ color: "#b45309", fontWeight: 600 }}>
          No subject schedule found for this group. Add subject sessions on the course price or course subject schedule first.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600, flex: 1, minWidth: 200 }}>
          Default: <strong>2 days before class at 9:00 PM (UK time)</strong>. Holiday weeks: <strong>2 weeks before the next teaching class</strong>. Mark holidays in <strong>Holiday Management</strong>.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={testingNotify ? <CircularProgress size={14} /> : <NotificationsActiveIcon />}
          disabled={testingNotify}
          onClick={sendTestNotification}
          sx={{ textTransform: "none", fontWeight: 600, borderColor: PURPLE, color: PURPLE }}
        >
          Send Test Notification
        </Button>
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700, mr: 0.5 }}>
          Subject:
        </Typography>
        <FilterChip label="All" active={subjectFilter === "all"} onClick={() => setSubjectFilter("all")} />
        {subjectSchedule.map((s) => (
          <FilterChip
            key={s.subject_id}
            label={s.subject_name}
            active={subjectFilter === String(s.subject_id)}
            onClick={() =>
              setSubjectFilter(subjectFilter === String(s.subject_id) ? "all" : String(s.subject_id))
            }
          />
        ))}
      </Stack>

      {flatRows.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>
            No deadlines for this subject.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #eef2f7" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 700 }}>Week</TableCell>
                {showSubjectColumn && <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Computed</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Override</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Final / Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flatRows.map(({ week, sub }) => {
                const st = statusChip(sub.status);
                const overrideVal =
                  overrideDrafts[week.id] ?? (week.deadline_override ? toDatetimeLocal(week.deadline_override) : "");
                return (
                  <TableRow
                    key={`${week.id}-${sub.subject_id}`}
                    sx={{ bgcolor: week.is_holiday ? "#fffbeb" : undefined }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        W{week.week_number}
                      </Typography>
                      {week.is_holiday && week.deadline_moved_to_week_number && (
                        <Chip
                          size="small"
                          label={`2 wks before W${week.deadline_moved_to_week_number}`}
                          sx={{
                            mt: 0.5,
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: "#fef3c7",
                            color: "#b45309",
                          }}
                        />
                      )}
                      {week.is_holiday && week.holiday_name && (
                        <Typography variant="caption" display="block" sx={{ color: "#b45309" }}>
                          {week.holiday_name}
                        </Typography>
                      )}
                    </TableCell>
                    {showSubjectColumn && <TableCell>{sub.subject_name}</TableCell>}
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {sub.held_weekday_name || "—"}
                        {sub.held_time ? ` · ${sub.held_time}` : ""}
                      </Typography>
                      {sub.class_date && (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Class: {sub.class_date}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDeadline(sub.computed_deadline)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          type="datetime-local"
                          size="small"
                          value={overrideVal}
                          onChange={(e) =>
                            setOverrideDrafts((prev) => ({ ...prev, [week.id]: e.target.value }))
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: 200 }}
                        />
                        <IconButton
                          size="small"
                          title="Clear override"
                          onClick={() => saveOverride(week.id, null)}
                          disabled={savingWeekId === week.id}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {overrideDrafts[week.id] !== undefined && (
                        <Button
                          size="small"
                          sx={{ mt: 0.5, textTransform: "none" }}
                          onClick={() => {
                            const v = overrideDrafts[week.id];
                            const iso = v ? new Date(v).toISOString() : null;
                            saveOverride(week.id, iso);
                          }}
                          disabled={savingWeekId === week.id}
                        >
                          Apply
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block" fontWeight={600}>
                        {formatDeadline(sub.deadline)}
                      </Typography>
                      <Chip
                        size="small"
                        label={st.label}
                        sx={{
                          mt: 0.5,
                          height: 20,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: st.bg,
                          color: st.color,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #eef2f7", bgcolor: "#f8fafc" }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b", display: "block", mb: 0.5 }}>
          Subject schedule (this group)
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {subjectSchedule.map((s) => (
            <Chip
              key={s.subject_id}
              size="small"
              label={`${s.subject_name}: ${s.held_weekday_name || "Day"} ${s.held_time || ""} (${s.duration_minutes || 60} min)`}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
